import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/common/BreadCrumb";
import { Badge, DropdownAddButton } from "../../components/micro/input";
import ReactSelect from "react-select";
import { useNavigate } from "react-router-dom";
import DebounceSearch from "../../components/common/DebounceSearch";
import CustomTable from "../../components/customTable/CustomTable";
import { SortDownArrow } from "../../assets/svg";
import Action from "../../components/common/Action";
import { useDispatch, useSelector } from "react-redux";
import { getServiceList, sendAllServiceList, updateMarketStatus } from "../../redux/service/slice";
import RippleLoader from "../../components/RippleLoader/RippleLoader";
import Toggles from "../../components/common/Toggles";

const Type = [
  { id: 1, value: "repair", label: "Repair" },
  { id: 2, value: "follow up repair", label: "Follow Up Repair" },
  { id: 3, value: "servicing", label: "Servicing" },
];

const ServiceRequest = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedBulk, setSelectedBulk] = useState("");
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [activeTab, setActiveTab] = useState("all");
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [modalMode, setModalMode] = useState(null);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  // Selector
  const { getServiceListData, loading } = useSelector((store) => ({
    getServiceListData: store?.serviceData?.getServiceListStatus?.data?.data,
    loading: store?.serviceData?.loading,
  }));

  useEffect(() => {
    dispatch(
      getServiceList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
        sales_status: selectedStatus?.value || "",
        types: selectedType?.value || "",
        status: activeTab,
      })
    );
  }, [
    searchValue,
    pageCount,
    pageLimit,
    activeTab,
    sortBy,
    sortDirection,
    selectedStatus,
    selectedType,
    activeTab,
  ]);

  const dropdownOptions = [
    {
      label: "1 Time Request",
      route: "/create-edit-technician-account",
      state: { mode: "createRequest" },
    },
    {
      label: "Contract Servicing",
      route: "/view-requests",
      state: { mode: "recurringRequest" },
    },
  ];

  const tabs = [
    { label: "All", value: "all" },
    { label: "Pending", value: "Pending" },
    { label: "Completed", value: "Completed" },
    { label: "Cancelled", value: "Cancelled" },
  ];

  const bulkOptions = () => {
    return [
      { id: 1, value: "pushedToSales", label: "Pushed to Sales" },
      { id: 2, value: "cancel", label: "Cancel" },
    ];
  };

  const handleBulkAction = (e) => {
    setSelectedBulk(e);

    const selectedIds = selectedRows.map((row) => row.id);

    const selectedData = getServiceListData?.data?.filter((item) =>
      selectedIds.includes(item.id)
    );

    if (!selectedData || selectedData.length === 0) return;

    if (e?.value === "pushedToSales") {
      const uniqueCompanies = [
        ...new Set(selectedData.map((item) => item?.id)),
      ];
      const payload = {
        ids: uniqueCompanies,
      };

      dispatch(sendAllServiceList(payload)).then((res) => {
        if (res.type === "sendAllServiceList/fulfilled") {
          dispatch(
            getServiceList({
              search: searchValue,
              pageCount: pageCount,
              pageLimit: pageLimit,
              sort_by: sortBy,
              sort_direction: sortDirection === "asc" ? "asc" : "desc",
              sales_status: selectedStatus?.value || "",
              types: selectedType?.value || "",
              status: activeTab,
            })
          );
          setIsSelectAll(false);
          setSelectedRows([]);
          setSelectedBulk("");
        } else {
          setIsSelectAll(false);
          setSelectedRows([]);
          setSelectedBulk("");
        }
      });
    } else {
      setIsSelectAll(false);
      setSelectedRows([]);
      setSelectedBulk("");
    }
  };

  const handleRowSelection = (id, isChecked) => {
    setSelectedRows((prevSelected) => {
      const updatedSelection = isChecked
        ? [...prevSelected, id]
        : prevSelected.filter((rowId) => rowId !== id);

      setIsSelectAll(
        updatedSelection.length === getServiceListData?.data?.length
      );

      return updatedSelection;
    });
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setIsSelectAll(checked);

    if (checked) {
      setSelectedRows(getServiceListData?.data?.map((item) => item));
    } else {
      setSelectedRows([]);
    }
  };

  const handleMarketStatusToggle = async (element) => {
    const newStatus = element?.is_market_active === 1 ? 0 : 1;
    
    try {
      const res = await dispatch(
        updateMarketStatus({
          id: element.id,
          data: {
            is_market_active: newStatus,
          },
        })
      );
      
      if (res.type === "updateMarketStatus/fulfilled") {
        dispatch(
          getServiceList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
            sales_status: selectedStatus?.value || "",
            types: selectedType?.value || "",
            status: activeTab,
          })
        );
      }
    } catch (error) {
      console.error("Error updating market status:", error);
    }
  };

  const ColumnHeaders = () => (
    <>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div className="flex items-center">
          <input
            type="checkbox"
            className="w-4 h-4 mr-2 accent-themeBtn"
            checked={isSelectAll}
            onChange={handleSelectAll}
          />
          <div
            className="flex items-center"
            onClick={() => {
              setSortBy("id");
              setSortDirection(sortDirection === "desc" ? "asc" : "desc");
            }}
          >
            <span>No.</span>
            <SortDownArrow
              className={`ml-1 transition-transform duration-300 ${
                sortDirection === "asc" && sortBy === "id" ? "rotate-180" : ""
              }`}
            />
          </div>
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("company");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Company</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "company"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("outlet");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Outlet</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "outlet" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("address");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Address</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "address"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4  text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("date");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Date & Time</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "date" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4  text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("technician");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Technician</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "technician"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4  text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("status");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Service Status </span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "status" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4  text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("sales");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Sales Status </span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "sales" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Types
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Status
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Action
      </th>
    </>
  );

  const DataRows = () => (
    <>
      {getServiceListData?.data?.map((element, index) => (
        <tr
          key={index}
          className="font-Proxima text-[14px] shadow-sm text-mgrey hover:bg-lightprimaryorange"
        >
          <td className="px-4 py-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id={`checkbox-${index}`}
                className="w-4 h-4 mr-2 accent-themeBtn"
                onChange={(e) => handleRowSelection(element, e.target.checked)}
                checked={selectedRows?.includes(element)}
              />
              {element?.unique_id || "-"}
            </div>
          </td>
          <td className="px-5 py-3">
            {element?.company?.name
              ? element.company.name
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())
              : "-"}
          </td>
          <td className="px-5 py-3">
            <div className="truncate-lines-3">
              {element?.outlets
                ?.map((tech) =>
                  tech?.outlet?.name
                    ?.toLowerCase()
                    ?.replace(/\b\w/g, (char) => char.toUpperCase())
                )
                .join(", ") || "-"}
            </div>
          </td>
          <td className="px-5 py-3">
            {element?.address
              ? element.address
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())
              : "-"}
          </td>
          <td className="px-5 py-3">
            {element?.date || "-"}
            <br />
            {element?.start_time && element?.end_time
              ? `${element.start_time} - ${element.end_time}`
              : "-"}
          </td>
          <td className="px-5 py-3 ">
            <div className="truncate-lines-3">
              {element?.technicians
                ?.map((tech) =>
                  tech.full_name
                    ?.toLowerCase()
                    ?.replace(/\b\w/g, (char) => char.toUpperCase())
                )
                .join(", ") || "-"}
            </div>
          </td>
          <td className="px-5 py-3">
            <div className="flex justify-start ">
              <Badge
                className={`capitalize whitespace-nowrap ${
                  element?.status === "completed"
                    ? "bg-bgGreen text-white"
                    : element?.status === "cancelled"
                    ? "border-borderRed text-red"
                    : element?.status === "pending"
                    ? "border-borderGrey text-black"
                    : element?.status === "accepted"
                    ? "bg-bgGreen text-white"
                    : element?.status == null
                    ? "border-none"
                    : element?.status === "accepted"
                    ? "bg-bgGreen text-white"
                    : element?.status === "approved"
                    ? "border-bgGreen text-bgGreen"
                    : ""
                }`}
                value={
                  element?.status === "cancelled"
                    ? "Cancelled"
                    : element?.status === "accepted"
                    ? "Accepted"
                    : element?.status === "completed"
                    ? "Completed"
                    : element?.status === "pending"
                    ? "Pending"
                    : element?.status === "accepted"
                    ? "Accepted"
                    : element?.status === "approved"
                    ? "Approved"
                    : element?.status == null
                    ? "-"
                    : "-"
                }
              />
            </div>
          </td>
          <td className="px-2 py-3">
            <div className="flex justify-start ">
              <Badge
                className={`capitalize whitespace-nowrap ${
                  element?.sales_status === "pushed_to_sales"
                    ? "border-borderGreen text-bgGreen !text-xs"
                    : element?.sales_status === "cancelled"
                    ? "border-borderRed text-red"
                    : element?.sales_status === "pending"
                    ? "border-borderGrey text-black"
                    : "-"
                }`}
                value={
                  element?.sales_status === "pushed_to_sales"
                    ? "Pushed to sales"
                    : element?.sales_status === "pending"
                    ? "Pending"
                    : "-"
                }
              />
            </div>
          </td>
          <td className="px-2 py-3">
            {element?.service_requests
              ?.map((tech) => tech?.service_request_type?.name)
              .join(", ") || "-"}
          </td>
          <td className="px-4 py-3">
            <Toggles
              checked={element?.is_market_active === 1}
              toggleFor={`marketStatus-${element?.id}`}
              onChange={() => handleMarketStatusToggle(element)}
            />
          </td>
          <td className="flex px-5 py-3">
            <Action
              optionList={
                <>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                    onClick={() =>
                      navigate("/view-service", {
                        state: { service: element },
                      })
                    }
                  >
                    View
                  </li>
                  <li
                    className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                  >
                    Cancel
                  </li>
                </>
              }
            />
          </td>
        </tr>
      ))}
    </>
  );

  const handleDropdownSelect = (option) => {
    setModalMode(option?.state?.mode);
    const navigationPath =
      option?.state?.mode === "recurringRequest"
        ? "/create-edit-recurring-service"
        : "/create-edit-service";

    navigate(navigationPath, {
      state: {
        modalMode,
      },
    });
  };

  return (
    <>
      <div>
        <div className="flex justify-between items-start">
          <div>
            <h6 className="font-Montserrat font-medium text-xl mb-3">
              Service Request
            </h6>
            <Breadcrumb
              breadCrumbParent={"Home"}
              breadCrumbActive={"Service Request"}
              breadCrumbTitleKey={"/"}
              classes=""
            />
          </div>
          <div>
            <DropdownAddButton
              value="Create Request"
              options={dropdownOptions}
              onClick={handleDropdownSelect}
            />
          </div>
        </div>
        <div className="flex justify-end mt-8">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              className={`py-2 w-full font-normal mb-5 h-full px-4 uppercase text-[14px] border-b-2 transition duration-300 font-Proxima ${
                activeTab === tab.value
                  ? "border-themeBtn text-themeBtn"
                  : "border-transparent text-textSecondary hover:text-themeBtn hover:border-theme "
              }`}
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.value}
            </button>
          ))}
        </div>
        <div className="flex items-center space-x-3">
          <ReactSelect
            className={"w-full max-w-52 text-black p-0 font-Proxima"}
            id="option"
            placeholder="Bulk Action"
            options={bulkOptions(selectedRows)}
            isClearable={true}
            value={selectedBulk}
            isDisabled={selectedRows?.length === 0}
            onChange={(e) => handleBulkAction(e)}
            styles={{
              control: (base) => ({
                ...base,
                border: `1px solid #0000003B`,
                borderColor: selectedBulk ? "#F15B29" : "#0000003B",
                borderRadius: "0.3rem",
                boxShadow: "none",
                "&:hover": {
                  border: "1px solid #0000003B",
                },
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? "#F15B29" : "white",
                "&:active": {
                  backgroundColor: state.isSelected
                    ? "#F15B29"
                    : "rgba(0, 0, 0, 0.04)",
                },
              }),
            }}
          />
          <ReactSelect
            className={"w-full max-w-52 text-black p-0 font-Proxima"}
            id="type"
            placeholder="Types"
            options={Type}
            isClearable={true}
            value={selectedType}
            onChange={(e) => {
              setSelectedType(e);
            }}
            styles={{
              control: (base) => ({
                ...base,
                border: `1px solid #0000003B`,
                borderColor: selectedType ? "#F15B29" : "#0000003B",
                borderRadius: "0.3rem",

                boxShadow: "none",
                "&:hover": {
                  border: "1px solid #0000003B",
                },
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? "#F15B29" : "white",
                "& :active": {
                  backgroundColor: state.isSelected
                    ? "#F15B29"
                    : "rgba(0, 0, 0, 0.04)",
                },
              }),
            }}
          />
          <DebounceSearch
            className="full-width"
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
        </div>
        {loading ? (
          <RippleLoader />
        ) : (
          <div className="w-full bg-white shadow rounded-lg mt-5 ">
            <CustomTable
              overflowVisibleLg={true}
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={getServiceListData}
              loading={false}
              showPagination={true}
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              current={pageCount}
              total={getServiceListData?.total}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(getServiceListData?.total / pageLimit)}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ServiceRequest;
